import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { ANIMATION_COMPLETE_EVENT } from '../../../types/chat';
import { AgentWithIcon } from '../../../types/chat';
import chatService from '../../../services/api/chatService';
import aiAgentService from '../../../services/api/aiAgentService';
import userService from '../../../services/api/userService';
import * as organizationService from '../../../services/api/organizationService';
import { apiRequest } from '../../../services/api/axiosConfig';
import { setUser } from '../../../redux/auth/authSlice';

/**
 * チャットページの副作用（useEffect）を管理するカスタムフック
 */
export const useChatEffects = (
  chatId: string | undefined,
  chatState: {
    isNewChat: boolean,
    setIsNewChat: (value: boolean) => void,
    messages: any[],
    setMessages: React.Dispatch<React.SetStateAction<any[]>>,
    agents: AgentWithIcon[],
    setAgents: (agents: AgentWithIcon[]) => void, 
    isLoadingAgents: boolean,
    setIsLoadingAgents: (value: boolean) => void,
    selectedAgent: AgentWithIcon | null,
    setSelectedAgent: (agent: AgentWithIcon | null) => void,
    setChats: (chats: any[]) => void,
    isLoading: boolean,
    setIsLoading: (value: boolean) => void,
    typingIndicator: boolean,
    setTypingIndicator: (value: boolean) => void,
    agentIdFromURL: number | null,
    setAgentIdFromURL: (value: number | null) => void,
    setOrgTokenInfo: (value: any) => void,
    setIsLoadingOrgInfo: (value: boolean) => void,
    // ThinkingProcess関連
    showThinkingProcess: boolean,
    setShowThinkingProcess: (value: boolean) => void,
    setThinkingMessage: (value: string) => void
  },
  user: any,
) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // リクエスト状態を追跡するref
  const chatRequestRef = useRef<{
    currentChatId: string | null,
    isRequested: boolean,
    hasChatData: boolean,
    isPolling: boolean  // ポーリング中フラグを追加
  }>({
    currentChatId: null,
    isRequested: false,
    hasChatData: false,
    isPolling: false
  });

  // ページロード時に直接URLからパスを確認してstateを設定
  useEffect(() => {
    // window.locationからパスを直接取得
    const path = window.location.pathname;
    // '/chat/new'で終わるかどうかをチェック
    const isNew = path.endsWith('/chat/new');
    
    // 状態を更新
    chatState.setIsNewChat(isNew);
  }, [chatState.setIsNewChat]);

  // URLからエージェントIDを取得（マウント時に一度だけ実行）
  useEffect(() => {
    console.log('isNewChat', chatState.isNewChat);
    if (chatState.isNewChat) {
      // window.locationから直接クエリ文字列を取得
      const queryString = window.location.search;
      
      // 手動でクエリパラメータを解析
      const urlParams = new URLSearchParams(queryString);
      
      // 'agentId'と'agent'の両方をチェック
      const rawAgentId = urlParams.get('agentId') || urlParams.get('agent');
      
      console.log('URLパラメータから取得したエージェントID:', rawAgentId);
      
      if (rawAgentId) {
        const parsedAgentId = Number(rawAgentId);
        
        if (!isNaN(parsedAgentId)) {
          // 有効な数値の場合のみ状態を更新
          console.log('有効なエージェントIDを設定:', parsedAgentId);
          chatState.setAgentIdFromURL(parsedAgentId);
        }
      }
    }
  }, [chatState.isNewChat, chatState.setAgentIdFromURL]);

  // URLが変更されたらisNewChatを更新
  useEffect(() => {
    // locationからもパスをチェック（React Router経由）
    const path = location.pathname;
    console.log('Current pathname from location:', path);

    const isNew = path.endsWith('/chat/new');
    console.log('Checking if new chat path from location:', isNew);
    chatState.setIsNewChat(isNew);

    // Super Agentモードの場合はリダイレクトをスキップ
    const isSuperAgentMode = path === '/chat/super-agent';

    // チャットIDがない、または不正な値の場合は新規チャットページにリダイレクト
    // ただし、Super Agentモードの場合は除外
    if (!isSuperAgentMode && chatId && chatId !== 'new' && isNaN(Number(chatId))) {
      navigate('/chat/new');
    }
  }, [chatId, location.pathname, navigate, chatState.setIsNewChat]);

  // ログイン状態のチェックと基本的なリダイレクト処理のみ
  useEffect(() => {
    const checkAuth = async () => {
      // tokenManagerを使用してリフレッシュトークンの存在を確認
      const tokenManager = (await import('../../../utils/tokenManager')).default;
      const hasRefreshToken = tokenManager.hasRefreshToken();
      
      if (!user && !hasRefreshToken) {
        console.log('ChatPage: No user and no refresh token, redirecting to login');
        // ここでのリダイレクトは、App.tsxのPrivateRouteですでに処理されている可能性が高いため
        // リダイレクトをログのみにして、実際のナビゲーションは無効化
        // navigate('/login');
      } else if (!user && hasRefreshToken) {
        console.log('ChatPage: No user but refresh token exists, not redirecting');
      }
    };
    
    checkAuth();
  }, [user, navigate]);

  // 注意: エージェントデータの取得は親コンポーネント(ChatPage)でReduxから行うため、
  // このフックでは行わない。これにより重複したAPI呼び出しを防止する。
  
  // チャット履歴取得 - 一度だけ実行
  useEffect(() => {
    const fetchChats = async () => {
      if (!user) return; // ユーザーがいない場合は何もしない
      
      try {
        const chatsData = await chatService.getUserChats();
        chatState.setChats(chatsData);
      } catch (error) {
        console.error('チャット履歴の取得に失敗:', error);
      }
    };
    
    fetchChats();
  }, [user?.id, chatState.setChats]); // チャット取得は user?.id によってのみトリガー
  
  // エージェント選択ロジック - エージェントデータが取得された後のみ実行
  useEffect(() => {
    // エージェントが既に選択されている場合やエージェントリストが空の場合は何もしない
    if (chatState.selectedAgent || chatState.agents.length === 0) {
      return;
    }
    
    const rawQueryString = window.location.search;
    const params = new URLSearchParams(rawQueryString);
    // 'agentId'と'agent'の両方をチェック
    const manualAgentId = params.get('agentId') || params.get('agent');

    // フォールバック時のデフォルトはGPT-6 Sol（存在しなければ先頭）
    const defaultAgent = chatState.agents.find(agent =>
      agent.model_type === 'gpt-6-sol' && agent.title === 'GPT-6 Sol'
    ) || chatState.agents.find(agent => agent.model_type === 'gpt-6-sol')
      || chatState.agents[0];
    
    console.log('エージェント選択ロジック: URLパラメータ =', manualAgentId);
    
    if (manualAgentId) { // URLに agentId または agent が指定されている場合
      const parsedAgentId = parseInt(manualAgentId, 10);
      const targetAgent = chatState.agents.find(agent => agent.id === parsedAgentId);
      if (targetAgent) {
        console.log('URLパラメータからエージェントを選択:', targetAgent.title, targetAgent.id);
        chatState.setSelectedAgent(targetAgent);
      } else if (chatState.agents.length > 0 && !chatState.isNewChat) {
        // URLのagentIdが無効で、既存チャットの場合はデフォルト（GPT-5.6 Terra優先）を選択
        console.log('URLのエージェントIDが無効、デフォルトエージェントを選択');
        chatState.setSelectedAgent(defaultAgent);
      }
      // URLのagentIdが無効で、新規チャットの場合は何も選択せずカードリストを表示する
    } else { // URLに agentId が指定されていない場合
      if (chatState.isNewChat) {
        // 新規チャットでURLにagentIdがない場合は、何も選択せずカードリストを表示する
        // chatState.setSelectedAgent(null); // selectedAgentが既にnullなら不要
        console.log('New chat without agentId in URL, showing agent list.');
      } else if (chatState.agents.length > 0) { // !isNewChat の場合
        // 既存チャットでURLにIDがない場合（通常は発生しないはず）、デフォルト（GPT-5.6 Terra優先）を選択
        chatState.setSelectedAgent(defaultAgent);
      }
    }
  }, [chatState.agents, chatState.selectedAgent, chatState.isNewChat, chatState.setSelectedAgent]);

  // ユーザーのトークン使用状況を取得
  useEffect(() => {
    const fetchTokenUsage = async () => {
      if (user && user.id) {
        try {
          // APIからプロファイル情報を直接取得（エラーが発生してもブロックしない）
          let profileData = {};
          try {
            const response = await apiRequest.get('/users/profile');
            const responseData = response as any;
            profileData = responseData.data && responseData.data.user ? responseData.data.user : {};
          } catch (profileError) {
            console.warn('[TokenUpdate] Error fetching profile:', profileError);
            // エラーがあっても処理を続行
          }
          
          // 使用統計も取得（エラーが発生してもブロックしない）
          let stats = { remaining_tokens: 0 };
          try {
            stats = await userService.getUserStats(user.id);
          } catch (statsError) {
            console.warn('[TokenUpdate] Error fetching user stats:', statsError);
            // エラーがあっても処理を続行
          }
          
          if (user) {
            // 既存値をデフォルトとして使用
            const backendTokenLimit = (profileData as any).token_limit;
            const currentTokenLimit = user.token_limit ?? 10000;
            const newTokenLimit = backendTokenLimit ?? currentTokenLimit;
            
            // 現在の使用量（ない場合は0）と新しい使用量を計算
            const currentTokensUsed = user.tokens_used ?? 0;
            const newTokensUsed = (profileData as any).tokens_used ?? (newTokenLimit - stats.remaining_tokens);
            
            // 値が変更された場合のみ更新
            if (currentTokensUsed !== newTokensUsed || currentTokenLimit !== newTokenLimit) {
              console.log('[TokenUpdate] Updating token info:', {
                oldLimit: currentTokenLimit,
                newLimit: newTokenLimit,
                oldUsed: currentTokensUsed,
                newUsed: newTokensUsed,
              });
              
              dispatch(setUser({
                ...user,
                token_limit: newTokenLimit,
                tokens_used: newTokensUsed,
              }));
            } else {
              console.log('[TokenUpdate] No changes in token values, skipping update');
            }
          }
        } catch (error) {
          // 致命的なエラーの場合のみログ出力（詳細な情報は出さない）
          console.error('[TokenUpdate] Fatal error in token usage fetch');
        }
      }
    };

    fetchTokenUsage();
  }, [user?.id, dispatch, user]); // 依存配列から user を削除

  // URLパラメータのchatIdが変更されたときに状態をリセット
  useEffect(() => {
    // chatIdが変わったときに実行されるロジック
    console.log(`チャットIDが変更されました: ${chatId}, 新規チャット: ${chatState.isNewChat}`);
    
    // チャットIDが変わった時は常にメッセージを初期化
    // ただし新規チャットで既にメッセージがある場合は保持する
    if (!(chatState.isNewChat && chatState.messages.length > 0)) {
      chatState.setMessages([]);
      console.log('チャットIDの変更を検知: メッセージをクリアしました');
    }
    
    // リクエスト状態をリセット
    chatRequestRef.current = {
      currentChatId: null,
      isRequested: false,
      hasChatData: false,
      isPolling: false
    };
  }, [chatId, chatState.isNewChat, chatState.setMessages]);

  // チャットデータの取得 - エージェントが揃った後のみ実行
  useEffect(() => {
    console.log(`チャットデータ取得 - チャットID: ${chatId}, 新規チャット: ${chatState.isNewChat}, メッセージ数: ${chatState.messages.length}`);
    
    // 以下の場合はAPIを呼ばない
    // 1. 新規チャットの場合
    // 2. chatIdが'new'の場合
    // 3. chatIdがない場合
    // 4. chatIdが数値に変換できない場合
    if (chatState.isNewChat || !chatId || chatId === 'new' || isNaN(Number(chatId))) {
      console.log('API呼び出しをスキップします。理由:', 
        chatState.isNewChat ? '新規チャット' : 
        !chatId ? 'chatIDなし' : 
        chatId === 'new' ? "chatID = 'new'" : 
        'chatIDが数値でない'
      );
      return;
    }
    
    // 数値変換できることを確認
    const numericChatId = Number(chatId);
    if (numericChatId <= 0) {
      console.log('無効なチャットID:', numericChatId);
      return;
    }

    // エージェント情報が準備できていない場合は実行しない
    if (chatState.agents.length === 0) {
      return;
    }

    // APIの呼び出し回数を減らすため、以下の条件をすべて満たす場合のみ実行
    // 1. 実行中でない
    // 2. 同じチャットIDに対して既にリクエストを行っていない
    // 3. チャットデータをまだ取得していない
    // 4. ポーリング中でない（追加）
    const isSameChatId = chatRequestRef.current.currentChatId === chatId;
    const shouldFetchData = !chatState.isLoading && 
                           (!isSameChatId || !chatRequestRef.current.isRequested) &&
                           !chatRequestRef.current.isPolling;  // ポーリング中は取得しない

    if (shouldFetchData) {
      console.log(`チャットID ${chatId} のリクエスト状態:`, {
        currentChatId: chatRequestRef.current.currentChatId,
        isRequested: chatRequestRef.current.isRequested,
        hasChatData: chatRequestRef.current.hasChatData,
        isPolling: chatRequestRef.current.isPolling,
        isSameChatId,
        shouldFetchData
      });

      // リクエスト状態を更新
      chatRequestRef.current = {
        currentChatId: chatId,
        isRequested: true,
        hasChatData: false,
        isPolling: false
      };
      
      const fetchChatMessages = async () => {
        try {
          chatState.setIsLoading(true);
          console.log(`チャットID ${chatId} のメッセージを取得します`);
          const chatData = await chatService.getChatById(Number(chatId));

          // リクエスト状態を更新（データ取得成功）
          chatRequestRef.current.hasChatData = true;
            
          // APIから取得したメッセージを適切な形式に変換
          if (chatData && chatData.messages) {
            console.log('APIから取得したメッセージデータの詳細を表示:', chatData.messages.slice(0, 2));
            
            // メッセージIDの重複を確認
            const messageIds = new Set();
            const duplicateIds = [];
            chatData.messages.forEach((msg: any) => {
              if (msg.id && messageIds.has(msg.id)) {
                duplicateIds.push(msg.id);
              }
              messageIds.add(msg.id);
            });
            
            if (duplicateIds.length > 0) {
              console.warn('重複したメッセージIDが検出されました:', duplicateIds);
            }
            
            // 既存のメッセージの状態を確認
            console.log('既存のメッセージ:', chatState.messages.map(m => ({ id: m.id, status: m.status })));
            
            // setMessagesの呼び出しをprevMessagesベースに変更
            chatState.setMessages(prevMessages => {
              // 既存のメッセージをMapに変換（IDをキーとして）
              const existingMessagesMap = new Map();
              prevMessages.forEach(msg => {
                if (msg.id) {
                  existingMessagesMap.set(msg.id, msg);
                }
              });
              
              console.log('既存メッセージMap:', Array.from(existingMessagesMap.keys()));
              
              // APIから取得したメッセージを処理
              const formattedMessages = chatData.messages.map((msg: any, index: number) => {
                // 全メッセージプロパティをログ出力（詳細なデバッグ用）
                if (index === 0) {
                  console.log('メッセージの全プロパティ一覧:', Object.keys(msg));
                  console.log('メッセージサンプル:', JSON.stringify(msg).substring(0, 200));
                }
                
                const messageId = msg.id || index + 1;
                
                // 既存のメッセージがある場合は、その内容を優先（特にstatus='completed'の場合）
                const existingMessage = existingMessagesMap.get(messageId);
                if (existingMessage && existingMessage.status === 'completed') {
                  console.log('既存のcompletedメッセージを保持:', messageId);
                  return existingMessage;
                }
                
                return {
                  id: messageId,
                  content: msg.content,
                  role: msg.role || (msg.sender_type === 'user' ? 'user' : 'assistant'), // roleフィールドを追加
                  sender_type: msg.sender_type || (msg.role === 'user' ? 'user' : 'ai'), // sender_typeまたはroleから変換
                  timestamp: msg.created_at || msg.timestamp || new Date().toISOString(), // timestampフィールドを追加
                  created_at: msg.created_at || msg.timestamp || new Date().toISOString(),
                  status: msg.status, // statusフィールドを追加
                  attachments: msg.attachments, // 添付ファイル情報を保持
                  uploaded_files: msg.uploaded_files, // アップロード済みファイル情報を保持
                  search_metadata: msg.search_metadata, // 検索メタデータを保持
                  tool_calls: msg.tool_calls // チャットツール実行履歴を保持（カード再表示用）
                };
              });
              
              console.log('変換後のメッセージ数:', formattedMessages.length);
              console.log('新しく取得したメッセージ:', formattedMessages.map(m => ({ id: m.id, status: m.status })));
              console.log('変換後のuploaded_files確認:', formattedMessages.map(m => ({ 
                id: m.id, 
                role: m.sender_type, 
                hasUploadedFiles: !!(m.uploaded_files && m.uploaded_files.length > 0),
                uploaded_files: m.uploaded_files
              })));
              
              return formattedMessages;
            });
            

            // チャットに関連するエージェントを設定（chatIdが存在する場合のみ）
            // chatIdが存在しない場合（/chat/aiや/chat/new）は、ChatPage側でエージェントを選択する
            if (chatId && chatData.agent_id && chatState.agents.length > 0) {
              const relatedAgent = chatState.agents.find(agent => agent.id === chatData.agent_id);
              if (relatedAgent) {
                chatState.setSelectedAgent(relatedAgent);
              }
            } else if (chatId && chatData.agent && chatState.agents.length > 0) {
              // agent関連のデータが直接含まれている場合（chatIdが存在する場合のみ）
              const agentData = chatData.agent;
              const relatedAgent = chatState.agents.find(agent => agent.id === agentData.id);
              if (relatedAgent) {
                chatState.setSelectedAgent(relatedAgent);
              }
            }
          } else {
            chatState.setMessages([]);
          }
        } catch (error) {
          console.error('チャットメッセージの取得に失敗:', error);
          // アラートを表示しない - ユーザー体験を損なう可能性があるため
          chatState.setMessages([]);
          
          // リクエスト状態をリセット（エラーの場合は再試行可能に）
          chatRequestRef.current.isRequested = false;
        } finally {
          chatState.setIsLoading(false);
        }
      };

      fetchChatMessages();
    }
  }, [chatId, chatState.isNewChat, chatState.agents.length]);

  // アニメーション完了イベントのリスナー
  useEffect(() => {
    const handleAnimationComplete = (event: Event) => {
      console.log('[ChatPage] アニメーション完了イベント受信');
      // タイピングインジケーターとローディング状態を解除
      chatState.setTypingIndicator(false);
      chatState.setIsLoading(false);
    };
    
    // イベントリスナーを追加
    window.addEventListener(ANIMATION_COMPLETE_EVENT, handleAnimationComplete);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener(ANIMATION_COMPLETE_EVENT, handleAnimationComplete);
    };
  }, [chatState.setTypingIndicator, chatState.setIsLoading]);

  // 組織のトークン情報を取得
  useEffect(() => {
    const fetchOrgTokenInfo = async () => {
      if (user?.organization_id) {
        try {
          chatState.setIsLoadingOrgInfo(true);
          const tokenInfo = await organizationService.getTokenUsageInfo(user.organization_id);
          chatState.setOrgTokenInfo(tokenInfo);
        } catch (error) {
          console.error('組織のトークン情報取得に失敗:', error);
        } finally {
          chatState.setIsLoadingOrgInfo(false);
        }
      }
    };

    fetchOrgTokenInfo();
  }, [user?.organization_id, chatState.setOrgTokenInfo, chatState.setIsLoadingOrgInfo]);

  // processingステータスのAIメッセージを検出してThinkingProcessを表示
  useEffect(() => {
    const checkProcessingMessages = async () => {
      console.log('processingメッセージ検出チェック:', {
        messages_length: chatState.messages.length,
        isNewChat: chatState.isNewChat,
        showThinkingProcess: chatState.showThinkingProcess,
        isPolling: chatRequestRef.current.isPolling
      });
      
      // メッセージがない場合のみスキップ（isNewChatとshowThinkingProcessの条件を削除）
      if (!chatState.messages.length) {
        console.log('processingメッセージ検出をスキップしました（メッセージなし）');
        return;
      }

      // processingステータスのAIメッセージを検索（'ai'または'assistant'）
      const processingMessage = chatState.messages.find(
        message => (message.sender_type === 'ai' || message.sender_type === 'assistant') && message.status === 'processing'
      );

      console.log('processingメッセージ検索結果:', {
        found: !!processingMessage,
        processingMessage,
        allMessages: chatState.messages.map(msg => ({
          id: msg.id,
          sender_type: msg.sender_type,
          status: msg.status
        }))
      });

      if (processingMessage) {
        console.log('processingメッセージを検出しました:', processingMessage.id);
        
        // 既にポーリング中の場合はスキップ
        if (chatRequestRef.current.isPolling) {
          console.log('既にポーリング中のためスキップ');
          return;
        }
        
        // ポーリング開始を記録
        chatRequestRef.current.isPolling = true;
        console.log('ポーリング開始 - API取得をブロック');
        
        // ThinkingProcessがまだ表示されていない場合のみ表示
        if (!chatState.showThinkingProcess) {
          console.log('ThinkingProcessを表示します');
          chatState.setShowThinkingProcess(true);
          chatState.setThinkingMessage('AI が応答を生成中です...');
        } else {
          console.log('ThinkingProcessは既に表示中です');
        }
        
        // ポーリングでAI応答を待つ
        const pollForResponse = async () => {
          try {
            const status = await chatService.getMessageStatus(processingMessage.id);
            
            if (status.status === 'completed') {
              console.log('processingメッセージが完了しました:', processingMessage.id);
              
              // メッセージ一覧を更新（重複チェック付き）
              chatState.setMessages(prevMessages => {
                const updated = prevMessages.map(msg => 
                  msg.id === processingMessage.id 
                    ? {
                        ...msg,
                        content: status.content || '',
                        status: 'completed',
                        suggestions: status.suggestions
                      }
                    : msg
                );
                
                // 重複を除去
                const uniqueMessages = [];
                const seenIds = new Set();
                for (const msg of updated) {
                  if (!seenIds.has(msg.id)) {
                    seenIds.add(msg.id);
                    uniqueMessages.push(msg);
                  } else {
                    console.warn('重複メッセージを除去:', msg.id);
                  }
                }
                
                return uniqueMessages;
              });
              
              // ThinkingProcessを終了
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
              
              // ポーリング終了を記録
              chatRequestRef.current.isPolling = false;
              console.log('ポーリング終了（完了） - API取得ブロック解除');
              
            } else if (status.status === 'failed') {
              console.error('processingメッセージが失敗しました:', processingMessage.id);
              
              // メッセージ一覧を更新
              chatState.setMessages(prevMessages => 
                prevMessages.map(msg => 
                  msg.id === processingMessage.id 
                    ? { ...msg, status: 'failed', content: 'エラーが発生しました。' }
                    : msg
                )
              );
              
              // ThinkingProcessを終了
              chatState.setShowThinkingProcess(false);
              chatState.setThinkingMessage('');
              
              // ポーリング終了を記録
              chatRequestRef.current.isPolling = false;
              console.log('ポーリング終了（失敗） - API取得ブロック解除');
              
            } else {
              // まだ処理中の場合、2秒後に再確認
              setTimeout(pollForResponse, 2000);
            }
          } catch (error) {
            console.error('processingメッセージのステータス取得に失敗:', error);
            
            // エラー時もThinkingProcessを終了
            chatState.setShowThinkingProcess(false);
            chatState.setThinkingMessage('');
            
            // ポーリング終了を記録
            chatRequestRef.current.isPolling = false;
            console.log('ポーリング終了（エラー） - API取得ブロック解除');
          }
        };
        
        // 2秒後にポーリング開始
        setTimeout(pollForResponse, 2000);
      }
    };

    checkProcessingMessages();
  }, [chatState.messages, chatState.isNewChat, chatState.setShowThinkingProcess, chatState.setThinkingMessage, chatState.setMessages]); // showThinkingProcessを依存配列から削除
};
